/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

/*
 * Story.jsx
 * Created by Destin Frasier on 12/10/20
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
    ContentContainerBodyShift,
    FlexRow,
    FlexCol,
    Heading,
    Button,
    Icon,
    SocialShareLink,
    Alert,
    Spinner,
    SlideShow,
    Inline,
} from 'sarsaparilla';
import { format } from 'date-fns';
import { useParams } from 'react-router';

import { useDocumentTitle } from 'shared-ui/utils/seo-utils';
import LocationsList from '../components/story/LocationsList';
import WinnerBanner from '../components/story/WinnerBanner';
import StaffBanner from '../components/story/StaffBanner';
import { fetchGalleryStory, fetchStorySearchLocations } from '../actions/galleryActions';

// Status can be any of these -
//grandPrize
//grandPrize2
//grandPrize3

//categoryWinnerTraditions
//categoryRunnerTraditions

//categoryWinnerReflectionJourneys
//categoryRunnerReflectionJourneys

//categoryWinnerCampervans
//categoryRunnerCampervans

//categoryWinnerGroupTravel
//categoryRunnerGroupTravel

//categoryWinnerActivitiesAdventure
//categoryRunnerActivitiesAdventure

//categoryWinnerBestTimeEver
//categoryRunnerBestTimeEver

//monthlyWinnerJan
//monthlyRunnerJan

//monthlyWinnerFeb
//monthlyRunnerFeb

//monthlyWinnerMar
//monthlyRunnerMar

//honorableMention

export default function UgcStory() {
    const { storyId } = useParams();
    const [story, setStory] = useState(null);
    const [loading, setLoading] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);
    const [searchLocations, setSearchLocations] = useState([]);

    const fetchStory = () => {
        if (story === null) {
            setLoading(true);
        }
        // fetch search results with applied filters
        fetchGalleryStory(storyId)
            .then((result) => {
                setStory(result?.data?.story);
                if (result?.data?.story?.locations) {
                    fetchStorySearchLocations(result?.data?.story?.locations)
                        .then((searchResponse) => {
                            setSearchLocations(searchResponse?.data?.results || []);
                        })
                        .catch(() => {
                            setHasErrors(true);
                        });
                }
                setLoading(false);
            })
            .catch(() => {
                setHasErrors(true);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchStory();
    }, []);

    const onLocationSelected = () => {};

    const backToGalleryButton = (
        <Button
            appearance="link"
            to="/shareyourstory"
            iconBeforeElement={<Icon iconName="arrow-back" />}
            className="mb-4"
        >
            Back to Story Gallery
        </Button>
    );

    const renderStory = () => {
        if (!story) {
            return null;
        }
        const categories = [story.final_category];
        const activities = story.activities;

        const images = story.media.map((m) => {
            return {
                media_id: m.media_id,
                altText: m.title,
                url: m.url,
                credit: `${story.first_name} ${story.last_name}`,
                position: m.position,
                created_date: m.created_date,
                updated_date: m.updated_date,
            };
        });

        const parts = story.text.split('\n');
        const paragraphs = parts.map((part, i) => {
            return <p key={`part-${i}`}>{part}</p>;
        });

        const submittedDate = story.submitted_date
            ? format(new Date(story.submitted_date), 'MMMM dd, yyyy')
            : '';
        const submittedYear = story.submitted_date
            ? format(new Date(story.submitted_date), 'yyyy')
            : '';

        return (
            <div>
                <FlexRow>
                    <FlexCol xs={12} md={8} className="ugc-story-page-body">
                        <FlexRow>
                            <FlexCol xs={12}>{backToGalleryButton}</FlexCol>

                            <FlexCol>
                                <FlexRow
                                    justifyContent="space-between"
                                    alignItems="center"
                                    className="mb-3"
                                >
                                    <FlexCol xs={6} md={6}>
                                        {categories.map((category, index) => (
                                            <div
                                                className="ugc-story-category-tag"
                                                key={index}
                                            >
                                                {category}
                                            </div>
                                        ))}
                                    </FlexCol>

                                    <FlexCol className="rec-share-btn-wrap">
                                        <Inline space="sm">
                                            <SocialShareLink
                                                platform="twitter-x"
                                                text={story?.title}
                                            />
                                            <SocialShareLink
                                                platform="facebook"
                                                text={story?.title}
                                            />
                                            <SocialShareLink
                                                platform="pinterest"
                                                text={story?.title}
                                            />
                                            <SocialShareLink
                                                platform="email"
                                                text={story?.title}
                                            />
                                        </Inline>
                                    </FlexCol>
                                </FlexRow>

                                <FlexRow
                                    justifyContent="space-between"
                                    alignItems="center"
                                    className="mb-3"
                                >
                                    <FlexCol xs={6} md={6}>
                                        {activities?.map((activity, index) => {
                                            return activity ? (
                                                <div
                                                    className="ugc-story-category-tag"
                                                    key={index}
                                                >
                                                    {activity}
                                                </div>
                                            ) : (
                                                <div />
                                            );
                                        })}
                                    </FlexCol>
                                </FlexRow>
                            </FlexCol>

                            <FlexCol xs={12}>
                                <article className="mb-6">
                                    <Heading
                                        headingLevel={1}
                                        appearance="h3"
                                        className="mb-1"
                                    >
                                        {story.title}
                                    </Heading>

                                    <ul className="ugc-story-author-date mb-4">
                                        <li>{submittedDate}</li>

                                        <li>
                                            By {`${story.first_name} ${story.last_name}`}
                                        </li>

                                        <li className="hide-item">
                                            {submittedYear} Share Your Story Contest
                                        </li>
                                    </ul>

                                    {story.is_staff_story && <StaffBanner />}

                                    {story.prizes_objs && (
                                        <>
                                            {story.prizes_objs.map((prize, i) => {
                                                return (
                                                    <WinnerBanner
                                                        key={`winner-type-${i}`}
                                                        winnerType={prize}
                                                        year={submittedYear}
                                                    />
                                                );
                                            })}
                                        </>
                                    )}

                                    <SlideShow
                                        images={images}
                                        status={'SUCCESS'}
                                        aspectRatio="16:9"
                                        className="mb-4"
                                    />

                                    {paragraphs}
                                </article>

                                {backToGalleryButton}
                            </FlexCol>
                        </FlexRow>
                    </FlexCol>

                    <FlexCol md={4} className="right">
                        <LocationsList
                            locations={searchLocations}
                            onCardSelect={onLocationSelected}
                        />
                    </FlexCol>
                </FlexRow>
            </div>
        );
    };

    useDocumentTitle(`Share Your Story Contest - Story - ${process.env.SITE_NAME}`);
    return (
        <main className="ugc-story-page">
            <ContentContainerBodyShift className="mb-10 mt-6">
                {hasErrors && (
                    <Alert
                        type="error"
                        heading={'There were errors processing request:'}
                        className="mb-3"
                    >
                        <ul>
                            <li>There were errors fetching story, please try again.</li>
                        </ul>
                    </Alert>
                )}

                {loading && <Spinner />}

                {story && renderStory(story)}
            </ContentContainerBodyShift>
        </main>
    );
}

UgcStory.propTypes = {
    location: PropTypes.shape({
        pathname: PropTypes.string,
        search: PropTypes.string,
        hash: PropTypes.string,
    }),
};
